"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import {
  CheckCircle2,
  ArrowRight,
  Monitor,
  Target,
  Clock,
  Shield,
  Lock,
  ClipboardCheck,
  BarChart3,
  FileText,
  Bot,
  Gauge,
  ChevronDown,
  ChevronUp,
} from "lucide-react"

const features = [
  {
    icon: ClipboardCheck,
    title: "Automated Risk Analysis",
    description: "AI-driven continuous monitoring identifies risks and vulnerabilities in real-time, automatically documenting findings and tracking remediation progress.",
  },
  {
    icon: FileText,
    title: "Policy & Procedure Generation",
    description: "Auto-generate customized HIPAA policies and procedures tailored to your organization. Keep documentation current with automated updates.",
  },
  {
    icon: Bot,
    title: "Virtual CISO Support",
    description: "Get AI-powered strategic security guidance and recommendations. Our vCISO platform provides expert-level compliance advice 24/7.",
  },
  {
    icon: Target,
    title: "Gap Remediation Roadmap",
    description: "Receive a prioritized, step-by-step compliance roadmap with clear action items, deadlines, and progress tracking.",
  },
  {
    icon: BarChart3,
    title: "Compliance Dashboard",
    description: "Real-time visibility into your compliance posture with executive-ready reports and audit-ready documentation.",
  },
  {
    icon: Shield,
    title: "Third-Party Risk Management",
    description: "Manage Business Associate Agreements and vendor compliance. Track and assess third-party risk automatically.",
  },
]

const stats = [
  { value: "70%", label: "Reduction in Manual Work", description: "Automate tedious compliance tasks" },
  { value: "60%", label: "Faster Assessments", description: "Complete risk assessments in record time" },
  { value: "50%+", label: "Time Savings", description: "Reclaim hours spent on compliance" },
  { value: "24/7", label: "Continuous Monitoring", description: "Always-on compliance surveillance" },
]

const faqs = [
  {
    question: "Can HIPAA Ready Pro certify us as HIPAA compliant?",
    answer: "There is no official HIPAA certification—unlike PCI-DSS or SOC 2, HIPAA has no governing body that grants certifications. Every organization is essentially self-certifying their compliance. What HIPAA Ready Pro provides is comprehensive documentation and compliance reports that demonstrate your organization follows all HIPAA guidelines, which you can present to auditors, business partners, and patients as evidence of compliance.",
  },
  {
    question: "What is HIPAA Ready Pro?",
    answer: "HIPAA Ready Pro is our comprehensive AI-powered compliance management platform designed specifically for healthcare organizations. It automates risk assessments, generates policies, provides virtual CISO guidance, and maintains continuous compliance monitoring—all in one integrated platform.",
  },
  {
    question: "How long does it take to get started?",
    answer: "We can onboard your organization onto the platform in as little as 24 hours. Our team handles the initial setup, configuration, and data migration. You'll have access to the platform immediately, with full implementation including customized policies and initial risk assessment completed within the first few weeks.",
  },
  {
    question: "Does HIPAA Ready Pro replace the need for a compliance officer?",
    answer: "HIPAA Ready Pro complements and empowers your compliance team by automating routine tasks, providing expert guidance, and handling documentation. It can serve as a virtual CISO for organizations without dedicated compliance staff, or enhance the capabilities of existing teams.",
  },
  {
    question: "What's included in the annual subscription?",
    answer: "The annual subscription includes: full platform access, automated risk assessments, policy generation and management, compliance dashboard, audit preparation tools, vendor risk tracking, security awareness training guidance, and email support. Premium support and custom integrations are available.",
  },
  {
    question: "Can HIPAA Ready Pro help with breach response?",
    answer: "Yes. The platform includes incident response planning templates, breach notification workflows aligned with HIPAA's 72-hour notification requirement, and documentation tools. Our support team can provide guidance during security incidents.",
  },
  {
    question: "Is the platform compliant with the January 2025 HIPAA updates?",
    answer: "Absolutely. HIPAA Ready Pro has been updated to address all new requirements including mandatory encryption, MFA implementation, 72-hour breach notification timelines, and enhanced network segmentation requirements.",
  },
  {
    question: "What support is included?",
    answer: "All subscriptions include email support with 24-hour response times. Premium support with phone access and dedicated account management is available for organizations requiring more hands-on assistance.",
  },
]

const processSteps = [
  {
    step: "1",
    title: "Connect & Assess",
    description: "Connect your systems and complete an automated comprehensive risk assessment in days, not weeks.",
    icon: Gauge,
  },
  {
    step: "2",
    title: "Generate & Customize",
    description: "AI generates customized policies, procedures, and documentation tailored to your organization.",
    icon: FileText,
  },
  {
    step: "3",
    title: "Remediate & Track",
    description: "Follow your prioritized remediation roadmap with automated progress tracking and alerts.",
    icon: Target,
  },
  {
    step: "4",
    title: "Monitor & Maintain",
    description: "Continuous monitoring ensures ongoing compliance with real-time dashboards and alerts.",
    icon: Shield,
  },
]

export default function HipaaReadyProContent() {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)

  // Load HubSpot Meetings embed script
  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js'
    script.async = true
    document.body.appendChild(script)

    // HubSpot's embed script injects an iframe with no title attribute, which
    // fails axe-core's frame-title rule. Watch for it and set an accessible name.
    const container = document.querySelector('.meetings-iframe-container')
    let observer: MutationObserver | null = null
    if (container) {
      observer = new MutationObserver(() => {
        const iframe = container.querySelector('iframe')
        if (iframe && !iframe.getAttribute('title')) {
          iframe.setAttribute('title', 'Schedule a HIPAA Ready Pro demo')
        }
      })
      observer.observe(container, { childList: true, subtree: true })
    }

    return () => {
      observer?.disconnect()
      document.body.removeChild(script)
    }
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#F0F9FF] to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-[#047857] text-white mb-4">AI-Powered HIPAA Compliance</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">
              HIPAA Ready Pro
            </h1>
            <p className="text-xl text-[#64748B] mb-8 max-w-2xl mx-auto">
              The comprehensive AI-powered compliance management platform that automates HIPAA compliance,
              reduces manual work by 70%, and keeps you audit-ready year-round.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a href="#schedule-demo">
                <Button size="lg" className="bg-[#0369A1] hover:bg-[#075985] text-white text-lg h-14 px-8">
                  Schedule Demo <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="/self-assessment">
                <Button size="lg" variant="outline" className="text-lg h-14 px-8">
                  Try Free Assessment
                </Button>
              </a>
            </div>

            {/* Pricing Badge */}
            <div className="inline-flex items-center gap-4 bg-white border-2 border-[#0EA5E9] rounded-xl px-6 py-4">
              <div className="text-left">
                <div className="text-3xl font-bold text-[#0F172A]">$3,500<span className="text-lg font-normal text-[#64748B]">/year</span></div>
                <div className="text-sm text-[#64748B]">
                  <span className="line-through">$5,000/year</span>
                  <Badge className="ml-2 bg-[#047857] text-white">30% Off First Year</Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#0EA5E9] mb-2">{stat.value}</div>
                <div className="text-white font-medium mb-1">{stat.label}</div>
                <div className="text-sm text-[#94A3B8]">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-[#E0F2FE] text-[#0284C7] mb-4">Platform Features</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
              Everything You Need for HIPAA Compliance
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              Our AI-powered platform handles the heavy lifting so you can focus on patient care.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 border-[#E2E8F0] hover:border-[#0EA5E9] transition-colors">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 rounded-xl bg-[#E0F2FE] flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-[#0EA5E9]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">{feature.title}</h3>
                  <p className="text-[#64748B]">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-[#F8FAFC]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-[#E0F2FE] text-[#0284C7] mb-4">How It Works</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
              Achieve Compliance in 4 Simple Steps
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              Our streamlined process gets you from assessment to audit-ready in weeks, not months.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <Card className="border-2 border-[#E2E8F0] h-full">
                  <CardContent className="pt-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                      {step.step}
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-[#E0F2FE] flex items-center justify-center mx-auto mb-4">
                      <step.icon className="h-6 w-6 text-[#0EA5E9]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0F172A] mb-2">{step.title}</h3>
                    <p className="text-sm text-[#64748B]">{step.description}</p>
                  </CardContent>
                </Card>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="h-6 w-6 text-[#CBD5E1]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* January 2025 Updates Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-[#FEF3C7] text-[#B45309] mb-4">January 2025 Updates</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
                Ready for the New HIPAA Requirements
              </h2>
              <p className="text-lg text-[#64748B]">
                HIPAA Ready Pro is fully updated to meet all new January 2025 Security Rule requirements.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-[#F59E0B] bg-[#FFFBEB]">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
                      <Lock className="h-5 w-5 text-[#D97706]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-2">Mandatory Encryption</h3>
                      <p className="text-sm text-[#64748B]">Our platform helps you implement and document encryption for data at rest and in transit, now required for all ePHI.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#F59E0B] bg-[#FFFBEB]">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
                      <Shield className="h-5 w-5 text-[#D97706]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-2">Multi-Factor Authentication</h3>
                      <p className="text-sm text-[#64748B]">MFA implementation guidance and compliance tracking for all systems accessing ePHI, now a mandatory requirement.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#F59E0B] bg-[#FFFBEB]">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-[#D97706]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-2">72-Hour Breach Notification</h3>
                      <p className="text-sm text-[#64748B]">Automated incident response workflows ensure you can meet the new stricter notification timeline.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#F59E0B] bg-[#FFFBEB]">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
                      <Monitor className="h-5 w-5 text-[#D97706]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-2">Network Segmentation</h3>
                      <p className="text-sm text-[#64748B]">Assessment tools and guidance for implementing enhanced network segmentation to isolate ePHI systems.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-[#F8FAFC]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-[#E0F2FE] text-[#0284C7] mb-4">FAQ</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border border-[#E2E8F0]">
                  <button
                    className="w-full text-left"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-[#0F172A] pr-4">{faq.question}</h3>
                        {expandedFaq === index ? (
                          <ChevronUp className="h-5 w-5 text-[#64748B] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-[#64748B] flex-shrink-0" />
                        )}
                      </div>
                      {expandedFaq === index && (
                        <p className="text-[#64748B] mt-4">{faq.answer}</p>
                      )}
                    </CardContent>
                  </button>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Demo Section */}
      <section id="schedule-demo" className="py-16 md:py-24 bg-gradient-to-br from-[#0284C7] to-[#0369A1]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Transform Your HIPAA Compliance?
            </h2>
            <p className="text-xl text-white mb-8">
              Schedule a personalized demo and see how HIPAA Ready Pro can automate your compliance workflow.
            </p>

            {/* HubSpot Meeting Scheduler */}
            <Card className="border-0 shadow-2xl">
              <CardContent className="pt-8 pb-8">
                <div
                  className="meetings-iframe-container"
                  data-src="https://hs.inventivehq.com/meetings/sean1324/hipaa-platform-demo?embed=true"
                  style={{ minHeight: '700px' }}
                />
              </CardContent>
            </Card>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-white">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-[#10B981]" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-[#10B981]" />
                <span>30-minute personalized demo</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-[#10B981]" />
                <span>Free compliance consultation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Not Ready for a Demo?</h2>
            <p className="text-[#64748B] mb-6">
              Try our free HIPAA self-assessment to understand your current compliance posture.
            </p>
            <a href="/self-assessment">
              <Button size="lg" variant="outline">
                Take Free Assessment <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
